import Event from 'src/models/Event';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'src/hooks/useRouter';
import EventSearch from 'src/services/EventSearch';
import { hasBackfillEvents } from 'src/helpers/eventListHelpers';
import { defaultHitsPerPage, parsePageWithZeroIndex } from '../../utils/pagination';
import { useRouteParams } from '../../lib/router';
import { usePluginConfig } from '../../contexts/pluginContext';

/*
 * This hook is used to fetch events from the API
 * It will fetch the first page of events and then fetch the next page when the user scrolls to the bottom of the page
 * It will also fetch the previous page when the user clicks on the "Load Previous" button
 * It will also determine if there are backfill events being included
 *
 * The select function will transform the data from raw events to Event objects
 * and will also let us know if this is the first page that includes backfill events
 */
export function useEventListQuery(initialRawEvents, initialPage) {
  const router = useRouter();
  const pluginConfig = usePluginConfig();
  const publisherSettings = pluginConfig.publisherSettings;
  const hitsPerPage = defaultHitsPerPage();
  console.debug('Router current state: ', router.query);
  console.debug('useEventListQuery Key: ', router.rawLocation);

  return useInfiniteQuery(
    ['eventsList', router.rawLocation],
    async ({ pageParam }) => {
      const eventSearch = new EventSearch({ context: router, pluginConfig });
      const rawEvents = await eventSearch.moreEvents(parsePageWithZeroIndex(pageParam.page));

      return {
        data: rawEvents,
        has_backfill_events: hasBackfillEvents(rawEvents),
        pageNumber: pageParam.page,
      };
    },
    {
      initialData: () => {
        return {
          pages: [
            {
              data: initialRawEvents,
              has_backfill_events: hasBackfillEvents(initialRawEvents),
              pageNumber: initialPage,
            },
          ],
        };
      },
      onError: (error) => {
        console.debug('Error occurred when fetching data', error);
      },
      select: (data) => {
        let alreadySeenBackfill = false;

        return {
          ...data,
          pages: data.pages.map((page) => {
            let firstBackfillPage = false;
            if (page.has_backfill_events && !alreadySeenBackfill) {
              alreadySeenBackfill = true;
              firstBackfillPage = true;
            }

            return {
              data: page.data.map((event) => new Event(event, publisherSettings)),
              has_backfill_events: page.has_backfill_events,
              pageNumber: page.pageNumber,
              first_backfill_page: firstBackfillPage,
            };
          }),
        };
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageNumber === 1) {
          return undefined;
        }
        return { page: firstPage.pageNumber - 1 };
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.data.length < hitsPerPage) {
          return undefined;
        }

        return { page: lastPage.pageNumber + 1 };
      },
      staleTime: Infinity,
    }
  );
}
